import { useEffect, useRef, useState } from 'react';

/**
 * useSessionState — drop-in replacement for useState that persists to sessionStorage
 * under a stable key. Restores prior value on mount; survives page navigation away & back
 * within the same browser tab. Cleared automatically when tab closes.
 *
 * Usage:
 *   const [search, setSearch] = useSessionState('tickets:search', '');
 *   const [filters, setFilters] = useSessionState('tickets:filters', { status: '', priority: '' });
 *
 * Notes:
 *   - The `initialValue` is only used when sessionStorage has no value for the key.
 *   - Stored values are JSON-serialized. Functions, Dates and React refs won't survive.
 *   - To clear, call setValue(initialValue) or sessionStorage.removeItem(key).
 */
export function useSessionState(key, initialValue) {
  const [value, setValue] = useState(() => {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw === null || raw === undefined) return initialValue;
      return JSON.parse(raw);
    } catch (e) {
      return initialValue;
    }
  });

  // Persist on every change (debounced via micro-task batch)
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      // Ensure key exists in storage even on first render (so back-nav restores)
      try {
        if (sessionStorage.getItem(key) === null) {
          sessionStorage.setItem(key, JSON.stringify(value));
        }
      } catch (e) { /* ignore quota */ }
      return;
    }
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (e) { /* ignore quota */ }
  }, [key, value]);

  return [value, setValue];
}

/** Clear all session-state keys with the given prefix (e.g. on logout). */
export function clearSessionStateByPrefix(prefix) {
  try {
    const keys = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(prefix)) keys.push(k);
    }
    keys.forEach(k => sessionStorage.removeItem(k));
  } catch (e) { /* ignore */ }
}
